import { AppContext, BookingData } from "../../../../../AppDataProvider/AppDataProvider";
import { Label, LineItem, Value } from "../common";
import { PaymentOption, useGetOrganisationPaymentQuery } from "../../../../../GeneratedGraphQL";
import React, { useContext } from "react";
import { BookingPrice } from "../getBookingPrices/getBookingPrices";
import { faCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PartRequired } from "../../../../../utils/types";
import { QuantityLine } from "../LineItems";
import { requireBookingDataFields } from "../../../../../utils";
import styled from "styled-components";
import { TotalLine } from "../LineItems/TotalLine/TotalLine";
import { UnvalidatedSummary } from "../UnvalidatedSummary/UnvalidatedSummary";
import { useTranslation } from "react-i18next";
export type ValidatedSummaryProps = {
  bookingPrices: BookingPrice[];
  getCurrencyDisplay: (n: number) => string;
};
type PaymentValidationBookingData = PartRequired<BookingData, "organisation" | "reservedSpaceId" | "service" | "timezone" | "userDetails">;
export const paymentValidationRequiredFields: (keyof BookingData)[] = ["organisation", "reservedSpaceId", "service", "timezone", "userDetails"];
const DiscountValue = styled(Value)`
  color: ${({
  theme
}) => theme.colours.dark};
`;
const Bullet = styled(FontAwesomeIcon).attrs({
  icon: faCircle
})`
  color: ${({
  theme
}) => theme.colours.success};
`;
export function ValidatedSummary(props: ValidatedSummaryProps) {
  const {
    bookingPrices,
    getCurrencyDisplay
  } = props;
  const {
    appData
  } = useContext(AppContext);
  const {
    i18n,
    t
  } = useTranslation();
  requireBookingDataFields<PaymentValidationBookingData>(appData.bookingData, paymentValidationRequiredFields);
  const {
    discountCode,
    organisation,
    pricePoints,
    reservedSpaceId,
    selectedBookings,
    service,
    tier,
    timezone,
    userDetails
  } = appData.bookingData;

  // Ignore lack of test coverage for locale "cimode" as it will only have "cimode" when in test mode
  /* istanbul ignore next */
  const locale = i18n.language !== "cimode" ? i18n.language : "en-GB";
  const {
    loading,
    data
  } = useGetOrganisationPaymentQuery({
    fetchPolicy: "network-only",
    variables: {
      bookingReservations: [{
        reservedSpaceId: reservedSpaceId,
        timezone: timezone,
        ...(selectedBookings && {
          bookingIds: selectedBookings.map(booking => booking._id)
        }),
        ...(tier && {
          tierId: tier._id
        }),
        ...(pricePoints && {
          pricePoints: pricePoints.map(pricePoint => {
            return {
              pricePointId: pricePoint.pricePoint._id,
              quantity: pricePoint.quantity
            };
          })
        })
      }],
      id: organisation._id,
      locale,
      ...(discountCode && {
        discount: {
          customerEmail: userDetails.email,
          discountCode
        }
      })
    }
  });

  // If we're still loading, don't render anything to prevent flicker between
  // unvalidated and validated summaries
  if (loading) return null;

  // This is just a summary, so displaying an error is likely to cause more confusion
  // than its worth. If the same error persists, the user will eventually be shown
  // an error on the payment step anyway, so just continue rendering the unvalidated summary.
  if (!data?.organisation?.payment) return <UnvalidatedSummary {...props} />;
  const {
    appliedDiscountValue,
    minChargeAdjustmentValue,
    paymentPrice,
    totalPrice
  } = data.organisation.payment;
  const {
    bookingPaymentOption,
    bookingDepositAmount
  } = service;
  const priceHasBeenModified = paymentPrice !== totalPrice;
  const bookingIsMultiPrice = bookingPrices.length !== 1;
  const bookingIsMultiAttendee = bookingIsMultiPrice || bookingPrices[0].quantity !== 1;
  return <>
      {/* If booking has more than one component price, show breakdown
       (using unverified data for this because it is just for information, and
       should match the verified data in any case ) */}
      {bookingIsMultiPrice && bookingPrices.map(({
      label,
      price: unitPrice,
      quantity
    }) => <QuantityLine key={unitPrice} {...{
      getCurrencyDisplay,
      label,
      quantity,
      unitPrice
    }} />)}

      {/* Subtotal line. Slightly different for single-price multi-attendee cases */}
      {priceHasBeenModified ? bookingIsMultiAttendee && !bookingIsMultiPrice ? <QuantityLine {...{
      getCurrencyDisplay,
      label: bookingPrices[0].label,
      overrideTotal: totalPrice,
      quantity: bookingPrices[0].quantity,
      unitPrice: bookingPrices[0].price
    }} /> : <LineItem>
            <Label>{t("subtotal")}</Label>
            <Value>{getCurrencyDisplay(totalPrice)}</Value>
          </LineItem> : null}

      {bookingPaymentOption === PaymentOption.Deposit ? <LineItem>
          <Label>
            {(parseInt(bookingDepositAmount) / 100).toLocaleString(locale, {
          style: "percent"
        })}{" "}
            {t("deposit")}
          </Label>
          <Value>{getCurrencyDisplay(paymentPrice)}</Value>
        </LineItem> : null}

      {appliedDiscountValue ? <LineItem>
          <Label>
            <Bullet />
            <span>{t("discountApplied")}</span>
          </Label>
          <DiscountValue>
            −{getCurrencyDisplay(appliedDiscountValue)}
          </DiscountValue>
        </LineItem> : null}

      {minChargeAdjustmentValue ? <LineItem>
          <Label>{t("minChargeAdjustment")}</Label>
          <Value>{getCurrencyDisplay(minChargeAdjustmentValue)}</Value>
        </LineItem> : null}

      {/* Final total line. Slightly different for unmodified single-price
       multi-attendee cases */}
      {bookingIsMultiAttendee && !bookingIsMultiPrice && !priceHasBeenModified ? <QuantityLine {...{
      getCurrencyDisplay,
      isTotal: true,
      label: bookingPrices[0].label,
      overrideTotal: paymentPrice,
      quantity: bookingPrices[0].quantity,
      unitPrice: bookingPrices[0].price
    }} /> : <TotalLine {...{
      getCurrencyDisplay,
      total: paymentPrice
    }} />}
    </>;
}
export default ValidatedSummary;